import React, { useState } from "react";
import {
  Card,
  Col,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Row,
  Space,
  TimePicker,
  Tooltip,
  Typography,
} from "antd";
import {
  BulbOutlined,
  InfoCircleOutlined,
  DownOutlined,
  RightOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";

import { DESCRIPTION_MAX_LENGTH } from "../model/configuration";

const { Text } = Typography;

interface RuleFieldsProps {
  form: FormInstance;
}

// ─── Live preview ────────────────────────────────────────────────────────────

interface RulePreviewProps {
  leadTimeDays: number;
  cutoffTime?: Dayjs | null;
  cutoffExtraDays: number;
}

const RulePreview: React.FC<RulePreviewProps> = ({
  leadTimeDays,
  cutoffTime,
  cutoffExtraDays,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // UI-only preview for parity with develop; this is not the canonical Order calculation.
  const orderDay = dayjs();
  const baseDays = Number(leadTimeDays) || 0;
  const extraDays = Number(cutoffExtraDays) || 0;

  // Earliest delivery when ordering BEFORE cutoff (or no cutoff at all)
  const deliveryNormal = orderDay.add(baseDays, "day");

  // Earliest delivery when ordering AFTER cutoff
  const deliveryLate = cutoffTime
    ? orderDay.add(baseDays + extraDays, "day")
    : null;

  const fmt = (d: dayjs.Dayjs) => d.format("M月D日(ddd)");
  const cutoffStr = cutoffTime ? cutoffTime.format("HH:mm") : null;

  // Build the lead-time label, e.g. "2日前"
  const leadLabel = baseDays > 0 ? `${baseDays}日前` : "当日中";

  return (
    <div className="rule-preview-box">
      <div
        className="rule-preview-header"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          cursor: "pointer",
          justifyContent: "space-between",
          userSelect: "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <BulbOutlined className="rule-preview-icon" />
          <Text strong style={{ fontSize: 12, color: "#7c3aed" }}>
            このルールの意味
          </Text>
        </div>
        {isOpen ? (
          <DownOutlined style={{ fontSize: 12, color: "#7c3aed" }} />
        ) : (
          <RightOutlined style={{ fontSize: 12, color: "#7c3aed" }} />
        )}
      </div>

      {isOpen && (
        <div className="rule-preview-body">
          {/* Plain-language rule explanation */}
          <div className="rule-preview-desc">
            <p className="rule-preview-desc-line">
              この商品を受け取るには、希望受取日の <strong>{leadLabel}</strong>{" "}
              までに注文が必要です。
            </p>
            {cutoffStr && (
              <p className="rule-preview-desc-line rule-preview-desc-line--warn">
                注文日の <strong>{cutoffStr}</strong> を過ぎて注文した場合は、
                受取可能日がさらに <strong>{extraDays}日</strong>{" "}
                後ろへ延びます。
              </p>
            )}
          </div>

          <div className="rule-preview-divider" />

          {/* Normal case */}
          <div className="rule-preview-example">
            <span className="rule-preview-badge rule-preview-badge--ok">
              例
            </span>
            <span>
              今日（<strong>{fmt(orderDay)}</strong>）
              {cutoffStr && (
                <>
                  {" "}
                  <span className="rule-preview-sub">{cutoffStr}より前</span> に
                </>
              )}
              {!cutoffStr && <>に</>}注文した場合 → 最短{" "}
              <strong className="rule-preview-date">
                {fmt(deliveryNormal)}
              </strong>{" "}
              に受取可
            </span>
          </div>

          {/* Late case (after cutoff) */}
          {cutoffStr && deliveryLate && (
            <div className="rule-preview-example rule-preview-example--warn">
              <span className="rule-preview-badge rule-preview-badge--warn">
                遅
              </span>
              <span>
                今日 <span className="rule-preview-sub">{cutoffStr}以降</span>{" "}
                に注文した場合 → +{extraDays}日延長 → 最短{" "}
                <strong className="rule-preview-date rule-preview-date--warn">
                  {fmt(deliveryLate)}
                </strong>{" "}
                に受取可
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const FieldLabel = ({ label, help }: { label: string; help: string }) => (
  <Space size={5}>
    <span>{label}</span>
    <Tooltip title={help}>
      <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
    </Tooltip>
  </Space>
);

export const DeadlineRuleFields: React.FC<RuleFieldsProps> = ({ form }) => {
  const cutoffTime = Form.useWatch("cutoff_time", form);
  const leadTimeDays = Form.useWatch("lead_time_days", form) ?? 0;
  const cutoffExtraDays = Form.useWatch("cutoff_extra_days", form) ?? 0;

  return (
    <>
      <Card style={{ marginBottom: 20, borderColor: "#ccc" }}>
        <Form.Item
          name="lead_time_days"
          label={
            <FieldLabel
              label="必要日数"
              help="希望受取日から何日前までに注文が必要かを指定します。"
            />
          }
          extra="0以上の日数を指定してください。"
          rules={[{ required: true, message: "必要日数を入力してください" }]}
        >
          <InputNumber
            min={0}
            max={32767}
            precision={0}
            addonAfter="日"
            style={{ width: "100%" }}
          />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="cutoff_time"
              label={
                <FieldLabel
                  label="当日締切時刻"
                  help="この時刻を過ぎた注文に、下の追加日数を加算するための判定時刻です。"
                />
              }
              extra="時刻による締切が不要な場合は空欄にします。"
            >
              <TimePicker
                format="HH:mm"
                minuteStep={5}
                placeholder="締切なし"
                style={{ width: "100%" }}
                onChange={(value) => {
                  if (!value) form.setFieldValue("cutoff_extra_days", 0);
                }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              name="cutoff_extra_days"
              label={
                <FieldLabel
                  label="締切後の追加日数"
                  help="締切時刻を過ぎた場合に、必要日数へさらに加算する日数です。"
                />
              }
              extra={
                cutoffTime
                  ? "締切を過ぎても加算しない場合は0日です。"
                  : "締切時刻を設定すると入力できます。"
              }
              rules={[
                { required: true, message: "追加日数を入力してください" },
              ]}
            >
              <InputNumber
                disabled={!cutoffTime}
                min={0}
                max={32767}
                precision={0}
                addonAfter="日"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Form.Item
        name="description"
        label={
          <FieldLabel
            label="メモ"
            help="この設定の対象や運用上の注意点を自由に記録できます。"
          />
        }
        extra="注文期限の計算には使用されない管理用メモです。"
      >
        <Input.TextArea
          rows={3}
          maxLength={DESCRIPTION_MAX_LENGTH}
          showCount
          placeholder="例：祝日前は営業担当へ確認"
        />
      </Form.Item>

      <RulePreview
        leadTimeDays={leadTimeDays}
        cutoffTime={cutoffTime}
        cutoffExtraDays={cutoffExtraDays}
      />

      <Text type="secondary" style={{ fontSize: 12 }}>
        ※ この内容は、選択した顧客と商品の注文期限設定に直接保存されます。
      </Text>
    </>
  );
};
